import React, { useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { User } from "../../models/users";
import { 
  useGetTenantsQuery,
  useBulkAssignUsersToTenantMutation,
  useRemoveUserFromTenantMutation,
  useUpdateUserTenantStatusMutation
} from "../../store/services/tenants";
import toast from "react-hot-toast";

interface TenantBodyTemplateProps {
  rowData: User;
  onUpdate?: () => void;
}

export const TenantBodyTemplate: React.FC<TenantBodyTemplateProps> = ({ 
  rowData, 
  onUpdate 
}) => {
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  
  // Get all available tenants
  const { data: availableTenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  
  // Mutation hooks
  const [bulkAssignUsersToTenant, { isLoading: isAssigning }] = useBulkAssignUsersToTenantMutation();
  const [removeUserFromTenant, { isLoading: isRemoving }] = useRemoveUserFromTenantMutation();
  const [updateUserTenantStatus, { isLoading: isUpdatingStatus }] = useUpdateUserTenantStatusMutation();

  // Get all current tenants assigned to user
  const currentTenants = rowData.tenants || [];
  const currentTenantIds = currentTenants.map(t => t.id);
  
  // Prepare tenant options for multiselect - show only provider
  const tenantOptions = Array.isArray(availableTenants) ? availableTenants?.map((tenant) => ({
    label: tenant.provider,
    value: tenant.id
  })) : [];

  const handleTenantClick = async (tenant: any) => {
    if (isEditing || isUpdatingStatus) return;

    try {
      const isCurrentlyActive = tenant.active === true;
      
      if (isCurrentlyActive) {
        // Deactivate the current tenant
        await updateUserTenantStatus({
          userId: rowData.id,
          tenantId: tenant.id,
          active: false
        }).unwrap();
        toast.success(`${tenant.provider} desactivado`);
      } else {
        // Activate this tenant and deactivate all others
        // First deactivate all currently active tenants
        const activeTenants = currentTenants.filter(t => t.active === true);
        for (const activeTenant of activeTenants) {
          await updateUserTenantStatus({
            userId: rowData.id,
            tenantId: activeTenant.id,
            active: false
          }).unwrap();
        }
        
        // Then activate the clicked tenant
        await updateUserTenantStatus({
          userId: rowData.id,
          tenantId: tenant.id,
          active: true
        }).unwrap();
        toast.success(`${tenant.provider} activado`);
      }
      
      if (onUpdate) onUpdate();
    } catch (error: any) {
      const errorMessage = error?.data?.err || "Error al cambiar estado del tenant";
      toast.error(errorMessage);
      console.error("Error updating tenant status:", error);
    }
  };

  const handleSave = async () => {
    try {
      // Find tenants to remove (current tenants not in selected)
      const tenantsToRemove = currentTenantIds.filter(id => !selectedTenants.includes(id));
      
      // Find tenants to add (selected tenants not in current)
      const tenantsToAdd = selectedTenants.filter(id => !currentTenantIds.includes(id));

      // Remove tenants that are no longer selected (individual calls)
      for (const tenantId of tenantsToRemove) {
        await removeUserFromTenant({
          tenantId,
          userId: rowData.id
        }).unwrap();
      }

      // Add new selected tenants using bulk endpoint
      for (const tenantId of tenantsToAdd) {
        await bulkAssignUsersToTenant({
          tenantId,
          userIds: [rowData.id]
        }).unwrap();
      }
      
      toast.success("Tenants actualizados exitosamente");
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error: any) {
      // Handle specific error messages from backend
      const errorMessage = error?.data?.err || "Error al actualizar los tenants";
      toast.error(errorMessage);
      console.error("Error updating tenants:", error);
    }
  };

  const handleCancel = () => {
    setSelectedTenants([]);
    setIsEditing(false);
  };

  const isLoading = isAssigning || isRemoving || isLoadingTenants || isUpdatingStatus;

  if (isLoadingTenants) {
    return <span className="text-gray-500">Cargando...</span>;
  }

  if (isEditing) {
    return (
      <div className="flex align-items-center gap-2">
        <MultiSelect
          value={selectedTenants}
          options={tenantOptions}
          onChange={(e) => setSelectedTenants(e.value)}
          placeholder="Seleccionar tenants"
          className="w-15rem"
          disabled={isLoading}
          display="chip"
        />
        <Button
          icon="pi pi-check"
          size="small"
          severity="success"
          onClick={handleSave}
          loading={isLoading}
          tooltip="Guardar"
        />
        <Button
          icon="pi pi-times"
          size="small"
          severity="secondary"
          onClick={handleCancel}
          disabled={isLoading}
          tooltip="Cancelar"
        />
      </div>
    );
  }

  return (
    <div className="flex align-items-center gap-2">
      <div className="flex gap-1 flex-wrap">
        {currentTenants.length > 0 ? (
          currentTenants.map((tenant) => (
            <Chip
              key={tenant.id}
              label={tenant.provider}
              className={`text-xs cursor-pointer transition-colors ${
                tenant.active 
                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => handleTenantClick(tenant)}
              style={{ 
                cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                userSelect: 'none'
              }}
            />
          ))
        ) : (
          <span className="text-gray-500">No asignado</span>
        )}
      </div>
      <Button
        icon="pi pi-pencil"
        size="small"
        text
        onClick={() => {
          setSelectedTenants(currentTenantIds);
          setIsEditing(true);
        }}
        tooltip="Editar tenants"
        disabled={isLoadingTenants || isUpdatingStatus}
      />
    </div>
  );
}; 